/**
 * Outgoings breakdown for the Revenue Calendar.
 *
 * The Monthly Outgoings table is the baseline: each row's amount is what is paid, and its
 * frequency is how often. The breakdown card restates each row's rate for the period picked:
 * a £269.62 monthly bill is £269.62 a month, £808.86 a quarter and £3,235.44 a year.
 * A row shows in a period only if it is active at some point in it.
 *
 * - Daily: paid per working day (Mon–Fri).
 * - Monthly / Quarterly / Annually: a monthly rate of amount, amount ÷ 3, amount ÷ 12.
 * - Once: the full amount in the period its start date falls in.
 *
 * VAT (20%, reclaimed) depends on the row's treatment: 'inclusive' amounts include it,
 * 'exclusive' amounts have it added on top, 'none' (insurance, wages) and unset have none.
 * Periods are worked out on the amount; asPaid / exVat then add or take off the VAT.
 * Profitability (and the Magic Number) uses the ex-VAT cost.
 *
 * A row with no end date runs indefinitely.
 */
export type OutgoingFrequency = 'Once' | 'Daily' | 'Monthly' | 'Quarterly' | 'Annually';

export type OutgoingRow = {
  amount: number;
  start_date: string;
  /** Null: no end date. */
  end_date: string | null;
  frequency: OutgoingFrequency;
  /** Null: not set, treated like 'none'. */
  vat_treatment?: VatTreatment | null;
};

export type VatTreatment = 'inclusive' | 'exclusive' | 'none';

export const OUTGOING_VAT_RATE = 0.2;

/** A figure worked out from the row's amount, as actually paid (with any VAT added on top). */
export function asPaid(row: Pick<OutgoingRow, 'vat_treatment'>, fromAmount: number): number {
  return row.vat_treatment === 'exclusive' ? fromAmount * (1 + OUTGOING_VAT_RATE) : fromAmount;
}

/** A figure worked out from the row's amount, without VAT. */
export function exVat(row: Pick<OutgoingRow, 'vat_treatment'>, fromAmount: number): number {
  return row.vat_treatment === 'inclusive' ? fromAmount / (1 + OUTGOING_VAT_RATE) : fromAmount;
}

/** A YYYY-MM-DD date as local midnight (new Date('YYYY-MM-DD') would be UTC). */
export function parseDateOnly(value: string): Date {
  const [y, m, d] = String(value || '').slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function dayOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Mon–Fri days from start to end, both inclusive. */
export function workingDaysBetween(start: Date, end: Date): number {
  let count = 0;
  for (let d = dayOnly(start); d <= end; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow >= 1 && dow <= 5) count += 1;
  }
  return count;
}

/** Mon–Fri days in a calendar month (month is 0-based). */
export function workingDaysInMonth(year: number, month: number): number {
  return workingDaysBetween(new Date(year, month, 1), new Date(year, month + 1, 0));
}

/** The row's rate per month (not used for Daily or Once). */
function monthlyRate(row: OutgoingRow): number {
  const amount = Number(row.amount) || 0;
  if (row.frequency === 'Quarterly') return amount / 3;
  if (row.frequency === 'Annually') return amount / 12;
  return amount;
}

/**
 * The row's cost for the period [start, end], where the period is `months` months long
 * (a week is 12/52 of a month). Zero if the row is not active in the period.
 */
function costForPeriod(row: OutgoingRow, start: Date, end: Date, months: number): number {
  const amount = Number(row.amount) || 0;
  const rowStart = parseDateOnly(row.start_date);
  if (row.frequency === 'Once') {
    return rowStart >= start && rowStart <= end ? amount : 0;
  }
  if (rowStart > end || (row.end_date && parseDateOnly(row.end_date) < start)) return 0;
  if (row.frequency === 'Daily') return amount * workingDaysBetween(start, end);
  return monthlyRate(row) * months;
}

/** The row's cost in one calendar month (month is 0-based). */
export function outgoingCostForMonth(row: OutgoingRow, year: number, month: number): number {
  return costForPeriod(row, new Date(year, month, 1), new Date(year, month + 1, 0), 1);
}

/** The row's cost over `count` months from `firstMonth` (0-based): a quarter is 3, a year 12. */
export function outgoingCostForMonths(row: OutgoingRow, year: number, firstMonth: number, count: number): number {
  return costForPeriod(row, new Date(year, firstMonth, 1), new Date(year, firstMonth + count, 0), count);
}

/** The row's cost in the Mon–Sun week starting `weekStart`: a 52nd of its yearly rate. */
export function outgoingCostForWeek(row: OutgoingRow, weekStart: Date): number {
  const start = dayOnly(weekStart);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return costForPeriod(row, start, end, 12 / 52);
}

/** The row's cost per working day in one month: that month's cost spread over its weekdays. */
export function outgoingCostPerWorkingDay(row: OutgoingRow, year: number, month: number): number {
  return outgoingCostForMonth(row, year, month) / Math.max(1, workingDaysInMonth(year, month));
}

/**
 * Average ex-VAT outgoings per working day over `months` months from `from`'s month:
 * everything due in that window divided by the weekdays in it. Steady from month to month,
 * unlike a single month's daily figure (months have 20–23 weekdays).
 */
export function averageWorkingDayOutgoings(rows: OutgoingRow[], from: Date, months = 12): number {
  let cost = 0;
  let days = 0;
  for (let i = 0; i < months; i += 1) {
    const d = new Date(from.getFullYear(), from.getMonth() + i, 1);
    days += workingDaysInMonth(d.getFullYear(), d.getMonth());
    cost += rows.reduce((sum, row) => sum + exVat(row, outgoingCostForMonth(row, d.getFullYear(), d.getMonth())), 0);
  }
  return days > 0 ? cost / days : 0;
}
